// ---------------------------------------------------------------------------
// AI-T.2: AI Agents page — 5 launch agents with human-in-the-loop approval
// ---------------------------------------------------------------------------

import { useState, useCallback } from "react";
import { Bot, Plus, Sparkles } from "lucide-react";
import { useAgentTasks, type AgentType } from "@/hooks/use-agent-tasks";
import { AgentTaskList } from "@/components/agents/agent-task-list";

// ---------------------------------------------------------------------------
// Agent config for the create dialog
// ---------------------------------------------------------------------------

const LAUNCH_AGENTS: ReadonlyArray<{
  type: AgentType;
  name: string;
  description: string;
  placeholder: string;
}> = [
  {
    type: "lead_qualifier",
    name: "Lead Qualifier",
    description: "Evaluate and score incoming leads against qualification criteria.",
    placeholder: "Qualify the new lead John Smith from Acme Corp",
  },
  {
    type: "review_responder",
    name: "Review Responder",
    description: "Draft professional responses to customer reviews.",
    placeholder: "Respond to the 3-star review about slow service",
  },
  {
    type: "appointment_reminder",
    name: "Appointment Reminder",
    description: "Send personalized appointment reminders with contextual details.",
    placeholder: "Send reminders for tomorrow's appointments",
  },
  {
    type: "email_drafter",
    name: "Email Drafter",
    description: "Draft personalized emails based on context and contact history.",
    placeholder: "Draft a follow-up email for the demo we had yesterday",
  },
  {
    type: "data_enrichment",
    name: "Data Enrichment",
    description: "Enrich contact profiles with missing data from available sources.",
    placeholder: "Enrich contacts missing phone numbers in the pipeline",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AiPage() {
  const {
    tasks,
    pendingApproval,
    isLoading,
    error,
    createTask,
    approveAction,
    rejectAction,
  } = useAgentTasks();

  const [showCreate, setShowCreate] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentType | null>(null);
  const [goal, setGoal] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = useCallback(async () => {
    if (!selectedAgent || !goal.trim()) return;
    setIsCreating(true);
    await createTask(selectedAgent, goal.trim());
    setIsCreating(false);
    setShowCreate(false);
    setGoal("");
    setSelectedAgent(null);
  }, [selectedAgent, goal, createTask]);

  const selectedConfig = LAUNCH_AGENTS.find((a) => a.type === selectedAgent);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--color-foreground)" }}>
            AI Agents
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--color-muted-foreground)" }}>
            Autonomous agents that propose actions for your approval. EU AI Act compliant.
          </p>
          <p className="text-xs mt-2" style={{ color: "var(--color-muted-foreground)" }}>
            Approved actions execute automatically via internal API calls. Failed actions stay explicit in task state.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg"
          style={{
            backgroundColor: "var(--color-primary)",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          <Plus size={16} />
          New Task
        </button>
      </div>

      {/* Create task panel */}
      {showCreate && (
        <div
          className="rounded-lg border p-4 space-y-4"
          style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-card)" }}
        >
          <h3 className="text-sm font-medium" style={{ color: "var(--color-foreground)" }}>
            Choose an Agent
          </h3>

          {/* Agent selector */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {LAUNCH_AGENTS.map((agent) => (
              <button
                key={agent.type}
                type="button"
                onClick={() => {
                  setSelectedAgent(agent.type);
                  setGoal("");
                }}
                className="flex items-start gap-3 p-3 rounded-lg border text-left transition-colors"
                style={{
                  borderColor: selectedAgent === agent.type ? "var(--color-primary)" : "var(--color-border)",
                  backgroundColor: selectedAgent === agent.type ? "var(--color-primary-light, rgba(16, 64, 204, 0.05))" : "transparent",
                  cursor: "pointer",
                }}
              >
                <div
                  className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: "var(--color-muted)" }}
                >
                  <Bot size={14} style={{ color: "var(--color-primary)" }} />
                </div>
                <div className="min-w-0">
                  <span className="text-sm font-medium block" style={{ color: "var(--color-foreground)" }}>
                    {agent.name}
                  </span>
                  <span className="text-xs block mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>
                    {agent.description}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Goal input */}
          {selectedAgent && (
            <div className="space-y-2">
              <label
                htmlFor="agent-goal"
                className="text-sm font-medium"
                style={{ color: "var(--color-foreground)" }}
              >
                Goal
              </label>
              <input
                id="agent-goal"
                type="text"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder={selectedConfig?.placeholder}
                className="w-full px-3 py-2 text-sm rounded-lg border"
                style={{
                  borderColor: "var(--color-border)",
                  backgroundColor: "var(--color-background)",
                  color: "var(--color-foreground)",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && goal.trim()) handleCreate();
                }}
              />
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!goal.trim() || isCreating}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg"
                  style={{
                    backgroundColor: goal.trim() && !isCreating ? "var(--color-primary)" : "var(--color-muted)",
                    color: goal.trim() && !isCreating ? "white" : "var(--color-muted-foreground)",
                    border: "none",
                    cursor: goal.trim() && !isCreating ? "pointer" : "not-allowed",
                  }}
                >
                  <Sparkles size={14} />
                  {isCreating ? "Creating..." : "Start Agent"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setSelectedAgent(null); setGoal(""); }}
                  className="px-3 py-2 text-sm font-medium rounded-lg"
                  style={{
                    backgroundColor: "transparent",
                    color: "var(--color-muted-foreground)",
                    border: "1px solid var(--color-border)",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div
          className="rounded-lg border px-4 py-3 text-sm"
          style={{
            borderColor: "var(--color-destructive)",
            backgroundColor: "color-mix(in srgb, var(--color-destructive) 5%, transparent)",
            color: "var(--color-destructive)",
          }}
        >
          {error}
        </div>
      )}

      {/* Task list with approval cards */}
      <AgentTaskList
        tasks={tasks}
        pendingApproval={pendingApproval}
        onApprove={approveAction}
        onReject={rejectAction}
        isLoading={isLoading}
      />
    </div>
  );
}
